import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ProviderPriceListProductUnlockModel, ProviderPriceListProductUnlockRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['providerPriceListProductUnlocks']

type ProviderPriceListProductUnlockFilters = {
  provider_price_list_id?: number,
  service_product_id?: number,
  unlock_price_list_id?: number,
  unlock_product_id?: number,
}

type UseProviderPriceListProductUnlocksOptions = {
  filters?: ProviderPriceListProductUnlockFilters,
  performHttpRequests?: boolean,
  requestOptions?: ProviderPriceListProductUnlockRequestOptions,
}

function useProviderPriceListProductUnlocks(options: UseProviderPriceListProductUnlocksOptions) {
  const { filters = {}, requestOptions } = options
  const {
    provider_price_list_id: filterProviderPriceListId,
    service_product_id: filterServiceProductId,
    unlock_price_list_id: filterUnlockPriceListId,
    unlock_product_id: filterUnlockProductId,
  } = filters

  const {
    updatedEntities: {
      providerPriceListProductUnlocks: providerPriceListProductUnlocksUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { providerPriceListProductUnlocks } = useSelector(reduxState => reduxState.entities)

  const filteredProviderPriceListProductUnlocks = useMemo(() => {
    const filtered = Object.values(providerPriceListProductUnlocks)
      .filter((providerPriceListProductUnlock: ProviderPriceListProductUnlockModel) => {
        const {
          provider_price_list_id,
          service_product_id,
          unlock_price_list_id,
          unlock_product_id,
        } = providerPriceListProductUnlock

        const matchProviderPriceListId = matchFilterNumber(provider_price_list_id, filterProviderPriceListId)
        const matchServiceProductId = matchFilterNumber(service_product_id, filterServiceProductId)
        const matchUnlockPriceListId = matchFilterNumber(unlock_price_list_id, filterUnlockPriceListId)
        const matchUnlockProductId = matchFilterNumber(unlock_product_id, filterUnlockProductId)

        return matchProviderPriceListId
          && matchServiceProductId
          && matchUnlockPriceListId
          && matchUnlockProductId
      })

    return sortArrayBy(filtered, 'desc', 'id')
  }, [providerPriceListProductUnlocksUpdatedAt, JSON.stringify(filters)])

  const filteredProviderPriceListProductUnlocksCount = filteredProviderPriceListProductUnlocks.length
  const hasFilteredProviderPriceListProductUnlocks = !!filteredProviderPriceListProductUnlocksCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredProviderPriceListProductUnlocksCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingProviderPriceListProductUnlocks } = useReduxAction(
    'providerPriceListProductUnlocks',
    'loadProviderPriceListProductUnlocks',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredProviderPriceListProductUnlocks,
    filteredProviderPriceListProductUnlocksCount,
    hasFilteredProviderPriceListProductUnlocks,
    loading: loadingProviderPriceListProductUnlocks,
  }
}

export default useProviderPriceListProductUnlocks
